import { Controller } from "@hotwired/stimulus"

/**
 * Controller for the ad account selector dropdown inside the drawer.
 *
 * Handles:
 * - Loading settings form for selected ad account
 * - Syncing with panel controller when panel's account changes
 * - Dispatching account-changed event when drawer dropdown changes
 */
export default class extends Controller {
  static targets = ["select"]

  static values = {
    baseUrl: String,
    frame: String,
    provider: String
  }

  connect() {
    // Listen for sync events from panel controller
    this.handlePanelSync = this.handlePanelSync.bind(this)
    this.element.addEventListener("attribution--panel:sync", this.handlePanelSync)

    // Also listen at document level for cross-component sync
    document.addEventListener("attribution--panel:sync", this.handlePanelSync)
  }

  disconnect() {
    this.element.removeEventListener("attribution--panel:sync", this.handlePanelSync)
    document.removeEventListener("attribution--panel:sync", this.handlePanelSync)
  }

  /**
   * Handle sync events from the panel controller.
   * Updates our dropdown to match the panel's selected account.
   */
  handlePanelSync(event) {
    if (!this.hasSelectTarget) return

    const { provider, adAccountId } = event.detail || {}
    if (this.providerValue !== provider) return

    // Update our select to match the panel's selection
    if (adAccountId && this.selectTarget.value !== String(adAccountId)) {
      this.selectTarget.value = adAccountId
      this.reloadFrame(adAccountId)
    }
  }

  /**
   * Handle change on the drawer's ad account dropdown.
   * Loads the settings form for the selected account.
   */
  change(event) {
    const adAccountId = event.target.value
    const selectedOption = event.target.options[event.target.selectedIndex]
    const accountAdAccountId = selectedOption?.dataset.accountAdAccountId

    // Load the settings form for this ad account
    this.reloadFrame(adAccountId)

    // Dispatch event to sync panel (bubbles up)
    this.dispatch("account-changed", {
      bubbles: true,
      prefix: "attribution",
      detail: {
        provider: this.providerValue,
        adAccountId,
        accountAdAccountId
      }
    })
  }

  reloadFrame(adAccountId) {
    const url = new URL(this.baseUrlValue, window.location.origin)
    url.searchParams.set("ad_account_id", adAccountId)

    const frame = document.getElementById(this.frameValue)
    if (frame) {
      frame.removeAttribute("src")
      requestAnimationFrame(() => {
        frame.src = url.toString()
      })
    }
  }
}
